import {
  Box,
  Button,
  ButtonGroup,
  Stack,
  Typography
} from '@mui/material';
import {
  CompanyYearlyDataTable,
  EpsBarChart
} from 'modules';

import _ from 'lodash';
import { useState } from 'react';
import * as ds from './dataService';



export type EpsAndRevenueTableProps = {
  variant: 'tab' | 'stockAnalysis';
  companyId: string;
  maxYearCount?: number;
};

export const FC = ({
  variant, companyId, maxYearCount
}: EpsAndRevenueTableProps) => {
  const { data } = ds.useQuery(companyId);
  const [
    showingEpsQoq,
    setShowingEpsQoq
  ] = useState(true);



  const yearsToShow = !_.isNil(maxYearCount)
    ? _.takeRight(data.yearsWithData, maxYearCount)
    : data.yearsWithData;



  return (
    <Stack
      width='100%'
      rowGap='1.5rem'
    >
      <Stack
        direction='row'
        justifyContent='space-between'
      >
        <Typography variant='tableHeader'>
          Earnings per share ({showingEpsQoq ? 'QoQ' : 'YoY'})
        </Typography>

      </Stack>

      {variant === 'tab' && (
        <Stack direction='row'>
          {yearsToShow.map((year, index) => {
            const chartData = data.earningsPerShare
              .filter((n) => n.year === year && n.period.includes('q')).map((d) => ({
                name: d.period,
                rate: d.value.toNumber()
              }));

            return (
              <Box
                key={year}
                sx={{
                  flex: '1 1 0'
                }}
              >
                <EpsBarChart.FC
                  key={year}
                  isFirst={index === 0}
                  rangeMin={data.epsRangeMin}
                  rangeMax={data.epsRangeMax}
                  year={year}
                  data={chartData}
                />
              </Box>
            );
          })}
        </Stack>
      )}

      <ButtonGroup
        size='small'
        sx={{
          '@media print': {
            display: 'none'
          }
        }}
      >
        <Button
          variant={showingEpsQoq ? 'contained' : 'outlined'}
          size='small'
          onClick={() => setShowingEpsQoq(true)}
        >Q/Q
        </Button>

        <Button
          variant={!showingEpsQoq ? 'contained' : 'outlined'}
          size='small'
          onClick={() => setShowingEpsQoq(false)}
        >Y/Y
        </Button>
      </ButtonGroup>

      <Stack direction='row'>
        {yearsToShow.map((year, index) => {
          const growthData = showingEpsQoq ? data.earningsQoqGrowth : data.earningsYoyGrowth;
          return (
            <CompanyYearlyDataTable.FC
              key={year}
              showQuarters={index === 0}
              dataPoints={data.earningsPerShare.filter((d) => d.year === year)}
              growthRate={growthData.filter((n) => n.year === year)}
            />
          );
        })}
      </Stack>

      <Typography variant='tableHeader'>
        Revenue
      </Typography>

      {variant === 'tab' && (
        <Stack direction='row'>
          {yearsToShow.map((year, index) => {
            const chartData = data.revenue
              .filter((n) => n.year === year && n.period.includes('q')).map((d) => ({
                name: d.period,
                rate: d.value.toNumber()
              }));

            return (
              <Box
                key={year}
                sx={{
                  flex: '1 1 0'
                }}
              >
                <EpsBarChart.FC
                  key={year}
                  isFirst={index === 0}
                  rangeMin={data.revenueRangeMin}
                  rangeMax={data.revenueRangeMax}
                  year={year}
                  data={chartData}
                />
              </Box>
            );
          })}
        </Stack>
      )}

      <Stack direction='row'>
        {yearsToShow.map((year, index) => {
          const growthData = data.revenueYoyGrowth;
          return (
            <CompanyYearlyDataTable.FC
              key={year}
              showQuarters={index === 0}
              dataPoints={data.revenue.filter((d) => d.year === year)}
              growthRate={growthData.filter((n) => n.year === year)}
            />
          );
        })}
      </Stack>
    </Stack>
  );
};
